import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { feedbackApi, type BoardItem, type FeedbackCategory, type MyFeedbackItem } from '@/lib/apiClient';
import { isNotFoundError } from '@/lib/httpErrors';
import { applyFollowState, followFailure, withId, withoutId } from '@/lib/everyonesFeedback';
import { BOARD_KEY, MINE_KEY, hasFeedbackLink, takeFeedbackLink, unreadCount, withoutFeedbackLink } from '@/lib/feedbackMine';
import { buildIndex, suggest } from '@/lib/similarFeedback';
import EveryonesFeedback, { type MeTooHandlers } from './EveryonesFeedback';
import MyFeedback, { type StopHandlers } from './MyFeedback';
import SimilarRequests from './SimilarRequests';

type Tab = 'send' | 'mine' | 'everyone';

const CATEGORIES: { key: FeedbackCategory; label: string; hint: string }[] = [
  { key: 'bug',      label: 'Bug',      hint: 'Something’s broken' },
  { key: 'idea',     label: 'Idea',     hint: 'A feature / change' },
  { key: 'question', label: 'Question', hint: 'How do I…?' },
];

const MAX_FILES = 5;
const MAX_BYTES = 15 * 1024 * 1024;
const ACCEPT = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv';
const fmtSize = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

const css = `
  .fb-btn {
    display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
    height: 26px; padding: 0 10px; border-radius: var(--ew-r-pill);
    background: transparent; color: rgba(255,255,255,0.75);
    border: 1px solid rgba(255,255,255,0.25); cursor: pointer;
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font);
    transition: color var(--ew-dur-fast) var(--ew-ease), border-color var(--ew-dur-fast) var(--ew-ease), background var(--ew-dur-fast) var(--ew-ease);
  }
  .fb-btn:hover { color: #fff; border-color: var(--ew-green); background: rgba(2,120,53,0.18); }
  .fb-badge {
    display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; padding: 0 5px;
    border-radius: var(--ew-r-pill); background: var(--ew-green); color: #fff; font: var(--ew-fw-semibold) 10.5px/1 var(--ew-font);
  }
  .fb-tabs { display: flex; gap: 4px; padding: 10px 18px 0; border-bottom: 1px solid var(--ew-n-200); }
  .fb-tab {
    display: inline-flex; align-items: center; gap: 6px; padding: 8px 12px; margin-bottom: -1px; cursor: pointer;
    background: transparent; border: 0; border-bottom: 2px solid transparent; color: var(--ew-n-500);
    font: var(--ew-fw-medium) 13px/1 var(--ew-font);
  }
  .fb-tab:hover { color: var(--ew-dark); }
  .fb-tab.active { color: var(--ew-dark); border-bottom-color: var(--ew-green); }
  .fb-scrim { position: fixed; inset: 0; background: rgba(0,0,0,0.25); z-index: 300; }
  .fb-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; width: 440px; max-width: 92vw; z-index: 301;
    background: #fff; box-shadow: -4px 0 24px rgba(0,0,0,0.14); display: flex; flex-direction: column; overflow-y: auto;
  }
  .fb-head {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 18px; border-bottom: 1px solid var(--ew-n-200); position: sticky; top: 0; background: #fff;
    font: var(--ew-fw-semibold) var(--ew-fs-body-lg)/1 var(--ew-font); color: var(--ew-dark);
  }
  .fb-x { background: transparent; border: 0; cursor: pointer; font-size: 16px; color: var(--ew-n-500); }
  .fb-body, .fb-done { padding: 18px; display: flex; flex-direction: column; gap: 10px; }
  .fb-done { align-items: center; text-align: center; gap: 14px; padding-top: 40px; }
  .fb-label { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-n-500); }
  .fb-cats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .fb-cat {
    display: flex; flex-direction: column; gap: 2px; align-items: flex-start; text-align: left;
    padding: 8px 10px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    background: #fff; cursor: pointer; color: var(--ew-dark);
  }
  .fb-cat span { font: var(--ew-fw-regular) 10.5px/1.2 var(--ew-font); color: var(--ew-n-500); }
  .fb-cat.active { border-color: var(--ew-green); background: var(--ew-green-50, #E6F2EB); }
  .fb-text { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); padding: 8px 10px; font: var(--ew-fw-regular) 13px/1.4 var(--ew-font); resize: vertical; min-height: 110px; }
  .fb-attach-row { display: flex; align-items: center; gap: 10px; }
  .fb-attach-btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md); background: #fff; cursor: pointer; font: var(--ew-fw-medium) 12px var(--ew-font); color: var(--ew-dark); }
  .fb-chips { display: flex; flex-direction: column; gap: 6px; }
  .fb-chip { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-sm); background: var(--ew-n-50); font: var(--ew-fw-regular) 12px var(--ew-font); }
  .fb-chip .nm { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--ew-dark); }
  .fb-chip .sz { color: var(--ew-n-500); }
  .fb-chip .rm { background: transparent; border: 0; cursor: pointer; color: var(--ew-n-500); font-size: 14px; }
  .fb-note { font: var(--ew-fw-regular) 11.5px/1.4 var(--ew-font); color: var(--ew-n-500); margin: 0; }
  .fb-err { font: var(--ew-fw-regular) 12px var(--ew-font); color: var(--ew-danger); margin: 0; }
  .fb-private { display: flex; align-items: center; gap: 8px; cursor: pointer; font: var(--ew-fw-semibold) 13px/1.3 var(--ew-font); color: var(--ew-dark); }
`;

/** Header "Feedback" button + a drawer with three tabs: send feedback, My feedback (what I sent, and what has been said about it), and
 *  Everyone's feedback (what other people have sent and an admin has chosen to share, without their names, where you can say "me too").
 *  Sending auto-attaches the current page; the server adds who/role. Supports pasted screenshots (Win+Shift+S → Ctrl+V)
 *  and file attachments (images + docs). Sending is blocked while impersonating (read-only).
 *
 *  The button carries the number of my items with something unread. And the drawer opens itself on My feedback when the page was
 *  opened from the link in an email (`?feedback=mine`): see `lib/feedbackMine`. */
export default function FeedbackButton() {
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('send');
  const [category, setCategory] = useState<FeedbackCategory>('idea');
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [warn, setWarn] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  // "Keep this private". It starts UNTICKED, which means the admins read what is sent first and may share it on the board, as the note beside it
  // says; ticked, nobody can. It is kept while the drawer stays open (a person who sent one sensitive thing is likely to send another) and
  // cleared when it closes.
  const [keepPrivate, setKeepPrivate] = useState(false);
  // What the last send came to, for the thank-you: decided by what was SENT, not by the box as it is by the time the answer comes back. Nothing
  // sent at submit time can put an item on the board (new feedback is reviewed by the admins first), so there are only two: private, or sent.
  const [sentAs, setSentAs] = useState<'private' | 'sent'>('sent');
  // Suggestions the person said are "not the same", for this message: forgotten when the box is emptied, and when it is sent.
  const [dismissed, setDismissed] = useState<ReadonlySet<number>>(new Set());
  // "Me too" and its undo: which items have one on the way, why the last one failed, and which were added during this visit.
  const [pendingIds, setPendingIds] = useState<ReadonlySet<number>>(new Set());
  const [meTooErrors, setMeTooErrors] = useState<ReadonlyMap<number, string>>(new Map());
  const [justAdded, setJustAdded] = useState<ReadonlySet<number>>(new Set());
  // "Stop following" on My feedback: why the last one failed, and what to tell a screen reader once one has gone. Which have one on the way is
  // `pendingIds`, the same set as "Me too" and its undo: both send the same call, so while either is on its way for an item neither is offered.
  const [stopErrors, setStopErrors] = useState<ReadonlyMap<number, string>>(new Map());
  const [stopNote, setStopNote] = useState('');
  // Bumped when the drawer is closed, so an answer that comes back AFTER that does not leave its words for the next time it is opened.
  const drawerVisit = useRef(0);
  const tabNow = useRef<Tab>(tab);
  tabNow.current = tab;
  const myTabRef = useRef<HTMLButtonElement>(null);
  const focusAfterStop = useRef(false);

  // ⚠️ IT HAS TO REFRESH ON ITS OWN. A number that is only right after a reload is no notification: someone answered at noon must find
  // out without reopening the app. The app turns refetch-on-focus off for every query, so this one turns it back on (still held
  // back by `staleTime`, so a quick tab switch costs nothing), polls slowly, and is refreshed when the drawer is opened.
  // `retry: 1`, not the default three: when the answer is an error (the API has not got the tables yet, say) every page load would
  // otherwise make four requests for a number on a button.
  const mine = useQuery({
    queryKey: MINE_KEY, queryFn: feedbackApi.mine, staleTime: 60_000, retry: 1,
    refetchOnWindowFocus: true, refetchInterval: 5 * 60_000,
  });
  const unread = unreadCount(mine.data);
  // `mutate` keeps its identity between renders, which the list's effect depends on. A failure (the read-only "view as" mode refuses
  // it) is left alone: the item simply stays unread.
  const seen = useMutation({
    mutationFn: (id: number) => feedbackApi.seen(id),
    onSettled: () => void qc.invalidateQueries({ queryKey: MINE_KEY }),
  });

  // What everyone may read, loaded once the drawer is open (nothing is fetched for a drawer nobody opens). ⚠️ SENDING NEVER DEPENDS ON IT:
  // if the board cannot be read, the Everyone's feedback tab says so and the suggestions are simply absent. `retry: 1` for the same
  // reason as the number on the button.
  const board = useQuery({ queryKey: BOARD_KEY, queryFn: feedbackApi.board, enabled: open, staleTime: 60_000, retry: 1 });

  /** Says "me too" (or undoes it) and keeps the board and My feedback in step with what the API answered. */
  const changeFollow = async (id: number, action: 'follow' | 'unfollow') => {
    setPendingIds((p) => withId(p, id));
    setMeTooErrors((m) => { const next = new Map(m); next.delete(id); return next; });
    try {
      const state = await (action === 'follow' ? feedbackApi.follow(id) : feedbackApi.unfollow(id));
      // The API's own answer, shown now; the refetch below makes it the board's.
      qc.setQueryData<BoardItem[]>(BOARD_KEY, (items) => applyFollowState(items, id, state));
      setJustAdded((a) => (action === 'follow' ? withId(a, id) : withoutId(a, id)));
      void qc.invalidateQueries({ queryKey: MINE_KEY });
    } catch (e) {
      setMeTooErrors((m) => new Map(m).set(id, followFailure(e)));
    } finally {
      setPendingIds((p) => withoutId(p, id));
      // Success or not, look again: a failure usually means the item is not what it was (closed, or off the board).
      void qc.invalidateQueries({ queryKey: BOARD_KEY });
    }
  };
  /**
   * Stops following an item on My feedback (`DELETE /feedback/:id/follow`, the same call as Undo "me too") and takes it off the list at once.
   * ⚠️ A 404 is the SAME RESULT for the person: the API says "not found" for an item they do not follow, so the list was out of date, and
   * the item goes from it all the same. Any other failure leaves the item and says why.
   */
  const stopFollowing = async (id: number) => {
    const visit = drawerVisit.current;
    // Whether the person is still where they pressed it: this drawer visit (not closed and opened again) and the My feedback tab. What is said,
    // and where focus goes, is for the list they are looking at, so none of it is done for one they have left.
    const here = () => visit === drawerVisit.current && tabNow.current === 'mine';
    setPendingIds((p) => withId(p, id));
    setStopErrors((m) => { const next = new Map(m); next.delete(id); return next; });
    setStopNote('');
    let gone: string | null = null;
    try {
      const state = await feedbackApi.unfollow(id);
      // The API's own answer on the board, as Undo "me too" does: until the refetch below lands, the board would go on saying "You're
      // following this" (with one follower too many) about an item the person has just stopped following.
      qc.setQueryData<BoardItem[]>(BOARD_KEY, (items) => applyFollowState(items, id, state));
      gone = 'You stopped following that request.';
    } catch (e) {
      if (isNotFoundError(e)) gone = 'That request was not on your list any more.';
      else setStopErrors((m) => new Map(m).set(id, followFailure(e))); // shown only at the confirmation, and forgotten when they start over
    } finally {
      setPendingIds((p) => withoutId(p, id));
      if (gone) {
        // Not following it, so not "added during this visit" either: left in, the Send tab would go on hiding "Not the same" for it and
        // saying the message "is still here, if you want to send it as well" about something that was not added.
        setJustAdded((a) => withoutId(a, id));
        // ⚠️ ONLY IF THE ITEM IS ON THE LIST NOW. Focus is lost when the list drops it, and the effect below runs when the list CHANGES; if
        // a refetch had already dropped it the list does not change, the effect never runs, and the flag would be left set for whatever
        // refresh comes next (the slow poll) to pull focus to the tab with nobody having done anything. And only if they are still HERE: for a
        // drawer that was closed and opened again, or another tab, a flag left set would take focus later from whatever they are doing.
        focusAfterStop.current = here() && qc.getQueryData<MyFeedbackItem[]>(MINE_KEY)?.some((i) => i.id === id) === true;
        qc.setQueryData<MyFeedbackItem[]>(MINE_KEY, (items) => items?.filter((i) => i.id !== id));
        // ⚠️ SAID ONLY WHERE IT CAN BE HEARD: a live region that is mounted later already holding the words is read out of nowhere.
        if (here()) setStopNote(gone);
      }
      // Either way look again: a refusal usually means the list is not what it was. And it is on the board's list differently now.
      void qc.invalidateQueries({ queryKey: MINE_KEY });
      void qc.invalidateQueries({ queryKey: BOARD_KEY });
    }
  };
  // The item that was open is gone from the page, and focus with it (to the top of the document). Once the list has changed, put it on the tab
  // the person is in, unless they have already moved it somewhere else.
  useEffect(() => {
    if (!focusAfterStop.current) return;
    focusAfterStop.current = false;
    const where = document.activeElement;
    if (!where || where === document.body) myTabRef.current?.focus();
  }, [mine.data]);
  // Leaving My feedback ends what was said there: it comes back as a fresh list, with a live region that has to be empty when it is made.
  useEffect(() => {
    if (tab !== 'mine') { setStopNote(''); setStopErrors((m) => (m.size ? new Map() : m)); }
  }, [tab]);
  const stop: StopHandlers = {
    pending: pendingIds, errors: stopErrors, onStop: (id) => void stopFollowing(id),
    onReset: (id) => setStopErrors((m) => { if (!m.has(id)) return m; const next = new Map(m); next.delete(id); return next; }),
  };

  const meToo: MeTooHandlers = {
    pending: pendingIds, errors: meTooErrors, justAdded,
    onMeToo: (id) => void changeFollow(id, 'follow'),
    onUndo: (id) => void changeFollow(id, 'unfollow'),
  };

  // What is on the board that reads like what is being typed. Matching is local and cheap (at most 200 items), and runs on a deferred
  // copy of the text so typing is never held up by it.
  const typed = useDeferredValue(message);
  // An emptied box is a fresh start: what was said to be "not the same" was about the message that has gone. ⚠️ Read off the DEFERRED text, not
  // the box: the suggestions are made from that text, so forgetting the dismissals any earlier would bring a dismissed one back for a render.
  useEffect(() => {
    if (!typed.trim()) setDismissed((d) => (d.size ? new Set() : d));
  }, [typed]);
  const index = useMemo(() => buildIndex(board.data ?? []), [board.data]);
  const suggestions = useMemo(() => suggest(index, typed, dismissed), [index, typed, dismissed]);

  // The link in an email. Once, on mount: this component is mounted when the person is signed in, so whether they had to sign in
  // first or not, the link is either still in the URL or was remembered by the sign-in screen. The parameter is then removed, so a
  // reload does not open the drawer again.
  useEffect(() => {
    if (!takeFeedbackLink(location.search)) return;
    setTab('mine');
    setOpen(true);
    if (hasFeedbackLink(location.search)) navigate({ search: withoutFeedbackLink(location.search) }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Land on what the number on the button counts: if something is unread, My feedback; otherwise the form. */
  const openDrawer = () => {
    setTab(unread > 0 ? 'mine' : 'send');
    setOpen(true);
    // Whatever has arrived since the last look: the list on screen refreshes in place.
    void qc.invalidateQueries({ queryKey: MINE_KEY });
    void qc.invalidateQueries({ queryKey: BOARD_KEY });
  };

  const addFiles = (incoming: File[]) => {
    setWarn(null);
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) { setWarn(`Up to ${MAX_FILES} files.`); break; }
      if (f.size > MAX_BYTES) { setWarn(`"${f.name}" is over ${fmtSize(MAX_BYTES)}.`); continue; }
      next.push(f);
    }
    setFiles(next);
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const imgs: File[] = [];
    for (const item of Array.from(e.clipboardData.items)) {
      if (item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) {
          const ext = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
          imgs.push(new File([f], f.name && f.name !== 'image.png' ? f.name : `screenshot-${Date.now()}.${ext}`, { type: item.type }));
        }
      }
    }
    if (imgs.length) { e.preventDefault(); addFiles(imgs); }
  };

  // ⚠️ THE CHOICE IS THE MUTATION'S VARIABLE, not the box as it is when the answer arrives: the thank-you says what was SENT.
  const mut = useMutation({
    mutationFn: (privateChoice: boolean) => feedbackApi.submit({
      category,
      message: message.trim(),
      page: window.location.pathname + window.location.search,
      files,
      // Only a TICK is sent (the type allows nothing else). Unticked is "the admins review it first", which is what the API does with no word on it.
      keepPrivate: privateChoice ? true : undefined,
    }),
    onSuccess: (_item, privateChoice) => {
      setSentAs(privateChoice ? 'private' : 'sent');
      setDone(true); setMessage(''); setFiles([]); setJustAdded(new Set()); // (an emptied box also forgets what was dismissed: see `typed`)
      // So the item is already in My feedback when they look. Not the board: nothing sent here is on it until an admin shows it.
      void qc.invalidateQueries({ queryKey: MINE_KEY });
    },
  });

  const close = () => {
    setOpen(false); setDone(false); setMessage(''); setFiles([]); setWarn(null); mut.reset();
    setKeepPrivate(false); setDismissed(new Set()); setJustAdded(new Set()); setMeTooErrors(new Map());
    setStopErrors(new Map()); setStopNote('');
    drawerVisit.current += 1;
  };

  return (
    <>
      <style>{css}</style>
      <button
        className="fb-btn"
        onClick={openDrawer}
        title="Send feedback about the Project Tracker, or see what you have sent and what you follow"
        aria-label={unread > 0 ? `Feedback, ${unread} unread` : undefined}
      >
        💬 Feedback{unread > 0 && <span className="fb-badge">{unread}</span>}
      </button>

      {open && (
        <>
          <div className="fb-scrim" onClick={close} />
          <div className="fb-drawer" role="dialog" aria-label="Feedback">
            <div className="fb-head">
              <span>Feedback</span>
              <button className="fb-x" onClick={close} aria-label="Close">✕</button>
            </div>

            {/* Plain buttons that report which is showing, like the Admin sub-tabs: not the full ARIA tab pattern, which also wants
                arrow-key movement and a labeled panel, so they do not claim to be tabs. */}
            <div className="fb-tabs">
              <button aria-pressed={tab === 'send'} className={`fb-tab${tab === 'send' ? ' active' : ''}`} onClick={() => setTab('send')}>
                Send feedback
              </button>
              <button
                ref={myTabRef}
                aria-pressed={tab === 'mine'}
                aria-label={unread > 0 ? `My feedback, ${unread} unread` : undefined}
                className={`fb-tab${tab === 'mine' ? ' active' : ''}`}
                onClick={() => setTab('mine')}
              >
                My feedback{unread > 0 && <span className="fb-badge">{unread}</span>}
              </button>
              <button aria-pressed={tab === 'everyone'} className={`fb-tab${tab === 'everyone' ? ' active' : ''}`} onClick={() => setTab('everyone')}>
                Everyone’s feedback
              </button>
            </div>

            {tab === 'mine' ? (
              <MyFeedback items={mine.data} isLoading={mine.isLoading} isError={mine.isError} onSeen={seen.mutate} stop={stop} announcement={stopNote} />
            ) : tab === 'everyone' ? (
              <EveryonesFeedback items={board.data} isLoading={board.isLoading} isError={board.isError} meToo={meToo} />
            ) : done ? (
              <div className="fb-done">
                <div style={{ fontSize: 30 }}>✅</div>
                <p style={{ margin: 0, color: 'var(--ew-dark)' }}>Thanks — we’ve got it. Feedback is reviewed regularly.</p>
                <p className="fb-note" style={{ margin: 0 }}>
                  {sentAs === 'private' && <>It’s private: only the admins can read it. Any reply shows up under <b>My feedback</b>.</>}
                  {sentAs === 'sent' && <>The admins read it first, and may share it on <b>Everyone’s feedback</b> without your name. Any reply shows up under <b>My feedback</b>.</>}
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button className="btn btn-secondary" onClick={() => { setDone(false); mut.reset(); }}>Send another</button>
                  <button className="btn btn-secondary" onClick={() => setTab('mine')}>See my feedback</button>
                  <button className="btn btn-primary" onClick={close}>Done</button>
                </div>
              </div>
            ) : (
              <div className="fb-body">
                <span className="fb-label">Type</span>
                <div className="fb-cats">
                  {CATEGORIES.map((c) => (
                    <button key={c.key} type="button" className={`fb-cat${category === c.key ? ' active' : ''}`} onClick={() => setCategory(c.key)}>
                      <b>{c.label}</b><span>{c.hint}</span>
                    </button>
                  ))}
                </div>

                <label className="fb-label" htmlFor="fb-msg">What’s on your mind?</label>
                <textarea
                  id="fb-msg"
                  ref={messageRef}
                  className="fb-text"
                  autoFocus
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onPaste={onPaste}
                  placeholder="Describe the bug, idea, or question — a sentence is plenty. Tip: snip with Win+Shift+S (draw a box), then paste here."
                />

                {/* ⚠️ ONE live region, always in the form: the panel below comes and goes as people type, and a screen reader does not announce a
                    section that mounts. This says that it has come, and how many. */}
                <span className="ew-sr-only" role="status" aria-label="Similar requests found">
                  {suggestions.length ? `${suggestions.length} similar request${suggestions.length === 1 ? '' : 's'} found, below the message box.` : ''}
                </span>

                <SimilarRequests
                  suggestions={suggestions}
                  meToo={meToo}
                  // The button that was pressed leaves the page with the suggestion, and focus with it: back to the message they are writing.
                  onNotTheSame={(id) => { setDismissed((d) => withId(d, id)); messageRef.current?.focus(); }}
                />

                <span className="fb-label">Attachments</span>
                <div className="fb-attach-row">
                  <button type="button" className="fb-attach-btn" onClick={() => fileRef.current?.click()}>📎 Attach file</button>
                  <span className="fb-note" style={{ margin: 0 }}>or paste a screenshot above · {files.length}/{MAX_FILES}</span>
                  <input ref={fileRef} type="file" multiple accept={ACCEPT} style={{ display: 'none' }}
                    onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
                </div>
                {files.length > 0 && (
                  <div className="fb-chips">
                    {files.map((f, i) => (
                      <div className="fb-chip" key={`${f.name}-${i}`}>
                        <span className="nm">{f.name}</span>
                        <span className="sz">{fmtSize(f.size)}</span>
                        <button className="rm" aria-label={`Remove ${f.name}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {warn && <p className="fb-err">{warn}</p>}

                <p className="fb-note">We’ll automatically include your name, role, and the page you’re on, for the admins.</p>
                <label className="fb-private">
                  <input type="checkbox" checked={keepPrivate} disabled={mut.isPending} onChange={(e) => setKeepPrivate(e.target.checked)} />
                  <span>Keep this private</span>
                </label>
                <p className="fb-note">
                  {keepPrivate
                    ? 'Only the admins will be able to read this.'
                    : 'The admins will read this first, and may share it with everyone signed in, without your name. Tick “Keep this private” for anything sensitive.'}
                </p>
                {mut.isError && <p className="fb-err">{(mut.error as Error).message}</p>}

                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={close}>Cancel</button>
                  <button className="btn btn-primary" disabled={!message.trim() || mut.isPending} onClick={() => mut.mutate(keepPrivate)}>
                    {mut.isPending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
